import { Component, ElementRef, HostListener, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

export interface FirmSelectorOption {
  firmId: number;
  firmName: string;
  firmCode?: number | null;
}

@Component({
  selector: 'app-firm-selector',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './firm-selector.component.html',
  styleUrl: './firm-selector.component.scss'
})
export class FirmSelectorComponent {
  firms = input<FirmSelectorOption[]>([]);
  selectedFirmId = input<number | null>(null);

  firmChange = output<number>();

  searchTerm = signal('');
  menuOpen = signal(false);
  private readonly searchThreshold = 7;

  filteredFirms = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) {
      return this.firms();
    }
    return this.firms().filter(firm => {
      const code = firm.firmCode == null ? '' : String(firm.firmCode);
      return firm.firmName.toLowerCase().includes(term) || code.includes(term);
    });
  });

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  shouldShowSearch(): boolean {
    return this.firms().length > this.searchThreshold;
  }

  selectedName(): string {
    const selectedId = this.selectedFirmId() ?? this.firms()[0]?.firmId ?? null;
    const firm = this.firms().find(item => item.firmId === selectedId);
    return firm?.firmName || 'Firm';
  }

  isSelected(firmId: number): boolean {
    const selectedId = this.selectedFirmId() ?? this.firms()[0]?.firmId ?? null;
    return firmId === selectedId;
  }

  toggleMenu(): void {
    this.menuOpen.update(open => !open);
    if (!this.menuOpen()) {
      this.searchTerm.set('');
    }
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  firmCodeLabel(firmCode: number | null | undefined): string | null {
    if (firmCode == null) {
      return null;
    }
    return `Firm code ${String(firmCode).padStart(4, '0')}`;
  }

  onSelect(firmId: number): void {
    this.firmChange.emit(firmId);
    this.searchTerm.set('');
    this.menuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    const target = event.target;
    if (target instanceof Node && this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.menuOpen.set(false);
    this.searchTerm.set('');
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (!this.menuOpen()) {
      return;
    }
    this.menuOpen.set(false);
    this.searchTerm.set('');
  }
}
